import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Grid,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  SkipNext,
  CheckCircle,
  Timer,
  PauseCircle,
  Headphones,
  MenuBook,
  Mic,
  Send,
  Close,
  Warning as WarningIcon,
  WifiOff,
  CloudUpload,
  CloudDone,
  ArrowForward
} from '@mui/icons-material';

import AudioRecorder from '../common/AudioRecorder';
import { API_ENDPOINTS, WS_BASE_URL } from '../../config/api';

interface Question {
  id: string;
  assignment_question_id: string;
  text: string;
  content: any;
  duration_minutes: number;
  max_score: number;
  parent_question_id?: string;
  section_name: string;
  previous_answer?: {
    answer: {
      question_id: string;
      answer_data: any;
      timestamp: string;
    };
    submitted_at: string;
    section_name: string;
    individual_submission: boolean;
  };
}

interface QuestionGroup {
  id: string;
  type: 'audio_comprehension' | 'reading_comprehension' | 'speaking' | 'single_question';
  parent_question?: Question;
  child_questions: Question[];
  estimated_time_minutes: number;
  total_questions: number;
}

interface Section {
  section_name: string;
  section_order: number;
  section_number: number;
  total_sections: number;
  question_groups: QuestionGroup[];
  total_questions: number;
  estimated_time_minutes: number;
}

const SectionBasedExamInterface: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId?: string }>();
  const navigate = useNavigate();

  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [examOverview, setExamOverview] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [isMediaPlaying, setIsMediaPlaying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(900); // Default 15 minutes, will be updated from exam_overview
  const [lastHeartbeat, setLastHeartbeat] = useState<Date>(new Date());
  const [pendingAnswers, setPendingAnswers] = useState<number>(0);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [timeWarningMessage, setTimeWarningMessage] = useState('');
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const websocketRef = useRef<WebSocket | null>(null);
  const answerQueueRef = useRef<Array<{ id: string, message: any, timestamp: number }>>([]);
  const reconnectAttemptsRef = useRef<number>(0);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const maxReconnectAttempts = 5;

  // Handle exam completion
  const handleExamComplete = useCallback(() => {
    navigate(`/exam/${assignmentId}/complete`);
  }, [assignmentId, navigate]);

  // Handle timer pause
  const handleTimerPause = useCallback((isPaused: boolean, reason?: string) => {
    // For now, just log - later integrate with actual timer
    console.log(`Timer ${isPaused ? 'paused' : 'resumed'}`, reason ? `(${reason})` : '');
  }, []);

  // Answer queue management
  const addToAnswerQueue = useCallback((answerId: string, message: any) => {
    const queueItem = {
      id: answerId,
      message,
      timestamp: Date.now()
    };
    answerQueueRef.current.push(queueItem);
    setPendingAnswers(answerQueueRef.current.length);
    console.log(`📥 Added answer to queue. Queue size: ${answerQueueRef.current.length}`);
  }, []);

  const removeFromAnswerQueue = useCallback((answerId: string) => {
    answerQueueRef.current = answerQueueRef.current.filter(item => item.id !== answerId);
    setPendingAnswers(answerQueueRef.current.length);
    console.log(`✅ Removed answer from queue. Queue size: ${answerQueueRef.current.length}`);
  }, []);

  const processAnswerQueue = useCallback(() => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.log('⚠️ Cannot process queue: WebSocket not open');
      return;
    }

    console.log(`🔄 Processing answer queue. Items: ${answerQueueRef.current.length}`);

    // Process all queued answers
    const itemsToProcess = [...answerQueueRef.current];
    itemsToProcess.forEach(item => {
      try {
        websocketRef.current?.send(JSON.stringify(item.message));
        console.log(`📤 Sent queued answer: ${item.id}`);
      } catch (error) {
        console.error(`❌ Failed to send queued answer ${item.id}:`, error);
      }
    });
  }, []);

  // Send answer with queue support
  const sendAnswerWithQueue = useCallback((assignmentQuestionId: string, questionId: string, answer: any) => {
    const answerMessage = {
      type: 'answer',
      assignment_question_id: assignmentQuestionId,
      answer: {
        question_id: questionId,
        answer_data: answer,
        timestamp: new Date().toISOString()
      }
    };

    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      try {
        websocketRef.current.send(JSON.stringify(answerMessage));
        console.log(`📤 Sent answer immediately for question ${assignmentQuestionId}`);
      } catch (error) {
        console.error(`❌ Failed to send answer, adding to queue:`, error);
        addToAnswerQueue(assignmentQuestionId, answerMessage);
      }
    } else {
      console.log(`⚠️ WebSocket not open, adding answer to queue`);
      addToAnswerQueue(assignmentQuestionId, answerMessage);
    }
  }, [addToAnswerQueue]);

  // Heartbeat mechanism
  const sendHeartbeat = useCallback(() => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      const heartbeatMessage = {
        type: 'heartbeat',
        timestamp: new Date().toISOString(),
        time_remaining: timeRemaining,
        current_section: currentSection?.section_name,
        answered_questions_count: answeredQuestions.size
      };

      try {
        websocketRef.current.send(JSON.stringify(heartbeatMessage));
        setLastHeartbeat(new Date());
        console.log('💓 Heartbeat sent');
      } catch (error) {
        console.error('❌ Failed to send heartbeat:', error);
      }
    }
  }, [timeRemaining, currentSection, answeredQuestions]);

  // Initialize WebSocket connection - ONLY once when component mounts
  useEffect(() => {
    // Prevent multiple connections - check if already connected
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      console.log('⚠️ WebSocket already connected, skipping initialization');
      return;
    }

    const connectWebSocket = () => {
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        setError(`Failed to connect after ${maxReconnectAttempts} attempts. Please refresh the page.`);
        return;
      }

      if (!assignmentId) {
        setError('Assignment ID is missing');
        return;
      }

      const wsUrl = API_ENDPOINTS.WEBSOCKET_EXAM(assignmentId);
      console.log(`🔌 Connecting to WebSocket (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}):`, wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('📡 WebSocket connected successfully');
        setConnectionStatus('connected');
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on success

        // Process any queued answers
        if (answerQueueRef.current.length > 0) {
          console.log(`🔄 Processing ${answerQueueRef.current.length} queued answers`);
          // Process queue inline to avoid dependency
          const itemsToProcess = [...answerQueueRef.current];
          itemsToProcess.forEach(item => {
            try {
              ws.send(JSON.stringify(item.message));
              console.log(`📤 Sent queued answer: ${item.id}`);
            } catch (error) {
              console.error(`❌ Failed to send queued answer ${item.id}:`, error);
            }
          });
        }

        // Start heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        heartbeatIntervalRef.current = setInterval(() => {
          // Send heartbeat inline to avoid dependency
          if (ws.readyState === WebSocket.OPEN) {
            const heartbeatMessage = {
              type: 'heartbeat',
              timestamp: new Date().toISOString()
            };
            try {
              ws.send(JSON.stringify(heartbeatMessage));
              setLastHeartbeat(new Date());
              console.log('💓 Heartbeat sent');
            } catch (error) {
              console.error('❌ Failed to send heartbeat:', error);
            }
          }
        }, 30000); // Send heartbeat every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', message.type);

          // Handle messages inline to avoid creating handleWebSocketMessage dependency
          switch (message.type) {
            case 'exam_overview':
              setExamOverview(message);
              // Update timer with actual time from backend
              if (message.time_remaining_seconds !== undefined) {
                setTimeRemaining(message.time_remaining_seconds);
                console.log(`⏱️ Timer initialized to ${message.time_remaining_seconds} seconds (${Math.floor(message.time_remaining_seconds / 60)} minutes)`);
              }
              setConnectionStatus('connected');
              break;
            case 'section_data':
              setCurrentSection(message);
              setConnectionStatus('connected');
              console.log(`📄 Loaded new section: ${message.section_name}`);
              // Scroll to top when new section loads
              window.scrollTo({ top: 0, behavior: 'smooth' });
              break;
            case 'section_transition':
              console.log(`🔄 Section transition: ${message.from_section} → ${message.to_section}`);
              // Reset timer for new section (section-wise timing)
              if (message.time_remaining_seconds !== undefined) {
                setTimeRemaining(message.time_remaining_seconds);
                console.log(`⏱️ Timer reset for ${message.to_section}: ${message.section_time_minutes} minutes`);
              }
              // Keep answeredQuestions intact - don't clear them between sections
              break;
            case 'answer_saved':
              const questionId = message.assignment_question_id;
              answerQueueRef.current = answerQueueRef.current.filter(item => item.id !== questionId);
              setPendingAnswers(answerQueueRef.current.length);
              setAnsweredQuestions(prev => new Set([...Array.from(prev), questionId]));
              setShowSaveNotification(true);
              setTimeout(() => setShowSaveNotification(false), 2000);
              console.log(`✅ Answer saved for question ${questionId}`);
              break;
            case 'heartbeat_ack':
              setLastHeartbeat(new Date());
              if (message.time_remaining !== undefined) {
                setTimeRemaining(message.time_remaining);
              }
              break;
            case 'exam_complete':
              console.log('🎉 Exam completed!');
              answerQueueRef.current = [];
              setPendingAnswers(0);
              navigate(`/exam/${assignmentId}/complete`);
              break;
            case 'exam_completed_already':
              console.log('⚠️ Exam already completed:', message.message);
              setError('This exam has already been completed. You cannot retake it.');
              setConnectionStatus('disconnected');
              // Redirect to completion page after showing message
              setTimeout(() => {
                navigate(`/exam/${assignmentId}/complete`);
              }, 3000);
              break;
            case 'section_incomplete':
              console.warn('⚠️ Section incomplete:', message.message);
              const errorMsg = message.message || `Please complete all questions before proceeding. ${message.missing_questions_count || 0} questions remaining.`;
              setError(errorMsg);
              setTimeout(() => setError(null), 5000);
              break;
            case 'error':
              console.error('❌ Exam error:', message.message);
              setError(message.message);
              if (message.fatal) {
                setConnectionStatus('disconnected');
              }
              break;
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
          setError('Failed to parse server message');
        }
      };

      ws.onclose = (event) => {
        console.log('📡 WebSocket disconnected. Code:', event.code, 'Reason:', event.reason);
        setConnectionStatus('disconnected');

        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Attempt to reconnect with exponential backoff
        if (event.code !== 1000) { // 1000 = normal closure
          reconnectAttemptsRef.current += 1;
          const backoffTime = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000); // Max 30 seconds
          console.log(`🔄 Attempting to reconnect in ${backoffTime / 1000} seconds...`);

          setTimeout(() => {
            connectWebSocket();
          }, backoffTime);
        }
      };

      ws.onerror = (error) => {
        console.error('📡 WebSocket error:', error);
        setError('Connection error. Attempting to reconnect...');
        setConnectionStatus('disconnected');
      };

      websocketRef.current = ws;
    };

    connectWebSocket();

    return () => {
      console.log('🔌 Component unmounting - closing WebSocket');
      if (websocketRef.current) {
        websocketRef.current.close(1000, 'Component unmounting'); // Normal closure
        websocketRef.current = null;
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [assignmentId, navigate]); // ONLY depend on assignmentId and navigate (stable dependencies)

  const handleWebSocketMessage = useCallback((message: any) => {
    console.log('📨 Received message:', message.type, message);

    switch (message.type) {
      case 'exam_overview':
        setExamOverview(message);
        setConnectionStatus('connected');
        break;

      case 'section_data':
        setCurrentSection(message);
        setConnectionStatus('connected');
        // Scroll to top when new section loads
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'answer_saved':
        // Individual answer was saved successfully
        const questionId = message.assignment_question_id;

        // Remove from queue if it was queued
        removeFromAnswerQueue(questionId);

        // Mark as answered
        setAnsweredQuestions(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.add(questionId);
          return newSet;
        });

        // Show save notification
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);

        console.log(`✅ Answer saved for question ${questionId}`);
        break;

      case 'answer_error':
        console.error(`❌ Error saving answer for question ${message.assignment_question_id}:`, message.error);

        // Retry the answer submission
        const queuedItem = answerQueueRef.current.find(item => item.id === message.assignment_question_id);
        if (queuedItem) {
          console.log(`🔄 Retrying answer submission for ${message.assignment_question_id}`);
          setTimeout(() => {
            if (websocketRef.current?.readyState === WebSocket.OPEN) {
              websocketRef.current.send(JSON.stringify(queuedItem.message));
            }
          }, 2000);
        }

        setError(`Failed to save answer: ${message.error}`);
        setTimeout(() => setError(null), 5000);
        break;

      case 'section_transition':
        console.log(`🔄 Section transition: ${message.from_section} → ${message.to_section}`);
        // Reset timer for new section (section-wise timing)
        if (message.time_remaining_seconds !== undefined) {
          setTimeRemaining(message.time_remaining_seconds);
          console.log(`⏱️ Timer reset for ${message.to_section}: ${message.section_time_minutes} minutes`);
        }
        break;

      case 'exam_complete':
        console.log('🎉 Exam completed!');

        // Clear any pending queue items
        answerQueueRef.current = [];
        setPendingAnswers(0);

        handleExamComplete();
        break;

      case 'heartbeat_ack':
        // Server acknowledged heartbeat
        setLastHeartbeat(new Date());
        console.log('💓 Heartbeat acknowledged');

        // Update time if server sends updated time
        if (message.time_remaining !== undefined) {
          setTimeRemaining(message.time_remaining);
        }
        break;

      case 'exam_completed_already':
        console.log('⚠️ Exam already completed:', message.message);
        setError('This exam has already been completed. You cannot retake it.');
        setConnectionStatus('disconnected');
        // Redirect to completion page after showing message
        setTimeout(() => {
          navigate(`/exam/${assignmentId}/complete`);
        }, 3000);
        break;

      case 'section_incomplete':
        console.warn('⚠️ Section incomplete:', message.message);
        const incompleteErrorMsg = message.message || `Please complete all questions before proceeding. ${message.missing_questions_count || 0} questions remaining.`;
        setError(incompleteErrorMsg);
        setTimeout(() => setError(null), 5000);
        break;

      case 'error':
        console.error('❌ Exam error:', message.message);
        setError(message.message);

        if (message.fatal) {
          setConnectionStatus('disconnected');
        }
        break;
    }
  }, [handleExamComplete, removeFromAnswerQueue]);

  // Timer countdown effect with warnings
  useEffect(() => {
    if (timeRemaining <= 0 || isMediaPlaying) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;

        // Time warnings (adjusted for 15-minute exam)
        if (newTime === 300 && !showTimeWarning) { // 5 minutes
          setTimeWarningMessage('⏰ 5 minutes remaining!');
          setShowTimeWarning(true);
          setTimeout(() => setShowTimeWarning(false), 5000);
        } else if (newTime === 120) { // 2 minutes
          setTimeWarningMessage('⚠️ Only 2 minutes left!');
          setShowTimeWarning(true);
          setTimeout(() => setShowTimeWarning(false), 5000);
        } else if (newTime === 60) { // 1 minute
          setTimeWarningMessage('🚨 Final minute! Please finish your current question.');
          setShowTimeWarning(true);
          setTimeout(() => setShowTimeWarning(false), 10000);
        }

        if (newTime <= 0) {
          // Time's up - auto submit exam
          handleExamComplete();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isMediaPlaying, handleExamComplete, showTimeWarning]);

  // Send individual answer to backend immediately with queue support
  const handleAnswerChange = useCallback((questionId: string, assignmentQuestionId: string, answer: any) => {
    // Update local state
    setAnswers(prev => ({
      ...prev,
      [assignmentQuestionId]: answer
    }));

    // Send to backend with queue support
    sendAnswerWithQueue(assignmentQuestionId, questionId, answer);
  }, [sendAnswerWithQueue]);

  // Handle media state changes (audio/video playing)
  const handleMediaStateChange = useCallback((isPlaying: boolean, mediaType?: string) => {
    setIsMediaPlaying(isPlaying);
    handleTimerPause(isPlaying, isPlaying ? `${mediaType || 'media'}_playing` : undefined);
  }, [handleTimerPause]);

  // Request next section from backend
  const handleRequestNextSection = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && currentSection) {
      const requestMessage = {
        type: 'request_next_section',
        current_section: currentSection.section_name
      };

      websocketRef.current.send(JSON.stringify(requestMessage));
      console.log(`📤 Requested next section after ${currentSection.section_name}`);

      // Scroll to top of page when moving to next section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getSectionIcon = (sectionName: string) => {
    const name = sectionName.toLowerCase();
    if (name.includes('reading')) return <MenuBook />;
    if (name.includes('listening') || name.includes('audio')) return <Headphones />;
    if (name.includes('speaking')) return <Mic />;
    return <CheckCircle />;
  };

  const getSectionColor = (sectionName: string) => {
    const name = sectionName.toLowerCase();
    if (name.includes('reading')) return 'secondary';
    if (name.includes('listening') || name.includes('audio')) return 'primary';
    if (name.includes('speaking')) return 'error';
    return 'default';
  };

  // Calculate section progress
  const getSectionProgress = () => {
    if (!currentSection) return 0;

    // Only count child questions for progress
    const allQuestionIds = currentSection.question_groups.flatMap(group =>
      group.child_questions.map(q => q.assignment_question_id)
    );
    const totalQuestions = allQuestionIds.length;

    const answeredInSection = allQuestionIds.filter(questionId =>
      answeredQuestions.has(questionId)
    ).length;

    return totalQuestions > 0 ? (answeredInSection / totalQuestions) * 100 : 0;
  };

  const canProceedToNextSection = () => {
    if (!currentSection) return false;

    // Only check child questions (parent questions are typically not answered separately)
    const allQuestionIds = currentSection.question_groups.flatMap(group =>
      group.child_questions.map(q => q.assignment_question_id)
    );

    const allAnswered = allQuestionIds.every(questionId => answeredQuestions.has(questionId));

    // Debug logging
    console.log('🔍 Section completion check:', {
      total_questions: allQuestionIds.length,
      answered_questions: Array.from(answeredQuestions).length,
      all_answered: allAnswered,
      question_ids: allQuestionIds,
      answered_ids: Array.from(answeredQuestions)
    });

    return allAnswered;
  };

  // Get all questions in current section (flattened with parent questions as separate items)
  const getAllQuestionsInSection = useCallback(() => {
    if (!currentSection) return [];

    const items: any[] = [];

    currentSection.question_groups.forEach(group => {
      // If there's a parent question, add it as the first item
      if (group.parent_question) {
        items.push({
          id: `parent_${group.id}`,
          is_parent: true,
          parent_question: group.parent_question,
          group_type: group.type,
          group_id: group.id
        });
      }

      // Then add all child questions
      group.child_questions.forEach(childQuestion => {
        items.push({
          ...childQuestion,
          is_parent: false,
          group_type: group.type,
          parent_question: group.parent_question,
          group_id: group.id
        });
      });
    });

    return items;
  }, [currentSection]);

  // Get current question being displayed
  const getCurrentQuestion = useCallback(() => {
    const allQuestions = getAllQuestionsInSection();
    return allQuestions[currentQuestionIndex] || null;
  }, [currentQuestionIndex, getAllQuestionsInSection]);

  // Navigation handler - Only Next (no previous allowed)
  const handleNextQuestion = () => {
    const allQuestions = getAllQuestionsInSection();
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reset question index when section changes
  useEffect(() => {
    setCurrentQuestionIndex(0);

    // Load previous answers when section changes
    if (currentSection) {
      const previousAnswersToLoad: Record<string, any> = {};

      currentSection.question_groups.forEach(group => {
        group.child_questions.forEach(question => {
          if (question.previous_answer?.answer?.answer_data) {
            previousAnswersToLoad[question.assignment_question_id] = question.previous_answer.answer.answer_data;
            // Also mark as answered
            setAnsweredQuestions(prev => new Set([...Array.from(prev), question.assignment_question_id]));
            console.log(`📝 Loaded previous answer for question ${question.assignment_question_id}:`, question.previous_answer.answer.answer_data);
          }
        });
      });

      if (Object.keys(previousAnswersToLoad).length > 0) {
        setAnswers(prev => ({ ...prev, ...previousAnswersToLoad }));
        console.log(`✅ Loaded ${Object.keys(previousAnswersToLoad).length} previous answers for section ${currentSection.section_name}`);
      }
    }
  }, [currentSection?.section_name]);

  if (connectionStatus === 'connecting') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography>Connecting to exam server...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        <Typography variant="h6">Exam Error</Typography>
        <Typography>{error}</Typography>
      </Alert>
    );
  }

  if (!currentSection) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading exam section...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Connection Status Banner */}
      {connectionStatus === 'disconnected' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WifiOff />}
        >
          <Typography variant="body2">
            <strong>Connection Lost</strong> - Attempting to reconnect...
            {pendingAnswers > 0 && ` (${pendingAnswers} answers pending)`}
          </Typography>
        </Alert>
      )}

      {/* Pending Answers Indicator */}
      {pendingAnswers > 0 && connectionStatus === 'connected' && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CloudUpload />}>
          <Typography variant="body2">
            Syncing {pendingAnswers} answer{pendingAnswers > 1 ? 's' : ''} with server...
          </Typography>
        </Alert>
      )}

      {/* Time Warning Snackbar */}
      <Snackbar
        open={showTimeWarning}
        autoHideDuration={5000}
        onClose={() => setShowTimeWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={timeRemaining < 120 ? 'error' : 'warning'}
          onClose={() => setShowTimeWarning(false)}
          sx={{ width: '100%' }}
        >
          {timeWarningMessage}
        </Alert>
      </Snackbar>

      {/* Save Notification Snackbar */}
      <Snackbar
        open={showSaveNotification}
        autoHideDuration={2000}
        onClose={() => setShowSaveNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" icon={<CloudDone />} sx={{ width: '100%' }}>
          Answer saved successfully
        </Alert>
      </Snackbar>

      {/* Section Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              icon={getSectionIcon(currentSection.section_name)}
              label={currentSection.section_name}
              color={getSectionColor(currentSection.section_name) as any}
              variant="outlined"
              size="medium"
            />
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              Section {currentSection.section_number} of {currentSection.total_sections}
            </Typography>
            <Chip
              icon={isMediaPlaying ? <PauseCircle /> : <Timer />}
              label={`Section Time: ${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')} ${isMediaPlaying ? '(Paused)' : ''}`}
              color={isMediaPlaying ? 'warning' : timeRemaining < 180 ? 'error' : 'success'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {/* Section Progress */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Section Progress: {currentSection.question_groups.flatMap(g => g.child_questions).filter(q => answeredQuestions.has(q.assignment_question_id)).length} of {currentSection.question_groups.flatMap(g => g.child_questions).length} questions answered
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(getSectionProgress())}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getSectionProgress()}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4
                }
              }}
            />
          </Box>

          {/* Time Management Alert */}
          {isMediaPlaying && (
            <Alert severity="info">
              <Typography variant="body2">
                ⏸️ <strong>Timer Paused:</strong> The exam timer is paused while you interact with media content.
                Take your time to listen/read carefully.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Exam Overview Stepper */}
      {examOverview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Exam Overview
            </Typography>
            <Stepper activeStep={currentSection.section_number - 1} alternativeLabel>
              {examOverview.sections.map((section: any, index: number) => (
                <Step key={section.section_name}>
                  <StepLabel
                    icon={getSectionIcon(section.section_name)}
                    optional={
                      <Typography variant="caption">
                        {section.total_questions} questions
                      </Typography>
                    }
                  >
                    {section.section_name}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Single Question View */}
      {(() => {
        const allQuestions = getAllQuestionsInSection();
        const currentItem = allQuestions[currentQuestionIndex];

        if (!currentItem) return null;

        // If current item is a parent question (reading passage/audio)
        if (currentItem.is_parent) {
          return (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {/* Header for Parent Question */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getSectionIcon(currentSection.section_name)}
                    {currentItem.group_type === 'reading_comprehension' ? 'Reading Passage' :
                      currentItem.group_type === 'audio_comprehension' ? 'Listening Audio' : 'Instructions'}
                  </Typography>
                  <Chip
                    label={`Step ${currentQuestionIndex + 1} of ${allQuestions.length}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {/* Audio Comprehension Parent */}
                {currentItem.group_type === 'audio_comprehension' && currentItem.parent_question.content?.audio_url ? (
                  <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Headphones color="primary" />
                      🎧 Listening Comprehension
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {currentItem.parent_question.text}
                    </Typography>
                    {currentItem.parent_question.content?.instructions && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Instructions:</strong> {currentItem.parent_question.content.instructions}
                        </Typography>
                      </Alert>
                    )}
                    <Box sx={{
                      p: 3,
                      backgroundColor: 'rgba(103, 126, 234, 0.1)',
                      borderRadius: 2,
                      border: '2px solid rgba(103, 126, 234, 0.3)'
                    }}>
                      <audio
                        controls
                        style={{ width: '100%', marginBottom: '16px' }}
                        onPlay={() => handleMediaStateChange(true, 'audio')}
                        onPause={() => handleMediaStateChange(false)}
                        onEnded={() => handleMediaStateChange(false)}
                      >
                        <source src={currentItem.parent_question.content.audio_url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Note:</strong> The timer will pause while the audio is playing. You can replay the audio as many times as needed.
                      </Typography>
                    </Box>
                  </Paper>
                ) : currentItem.group_type === 'reading_comprehension' ? (
                  <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MenuBook color="primary" />
                      📖 Reading Passage
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', mb: 2 }}>
                      {currentItem.parent_question.content?.paragraph ||
                        currentItem.parent_question.content?.passage ||
                        currentItem.parent_question.text}
                    </Typography>
                    {currentItem.parent_question.content?.instructions && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Instructions:</strong> {currentItem.parent_question.content.instructions}
                        </Typography>
                      </Alert>
                    )}
                  </Paper>
                ) : (
                  <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                      📝 Question Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {currentItem.parent_question.text}
                    </Typography>
                    {currentItem.parent_question.content?.instructions && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Instructions:</strong> {currentItem.parent_question.content.instructions}
                        </Typography>
                      </Alert>
                    )}
                  </Paper>
                )}

                {/* Continue Button for Parent Questions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={handleNextQuestion}
                    size="large"
                  >
                    Continue to Questions
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        }

        // If current item is a child question
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* Question Navigation Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getSectionIcon(currentSection.section_name)}
                  Question {currentQuestionIndex + 1} of {allQuestions.length}
                </Typography>
                <Chip
                  label={currentItem.group_type?.replace('_', ' ').toUpperCase() || 'Question'}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Current Question */}
              <Paper sx={{
                p: 3,
                border: Array.from(answeredQuestions).includes(currentItem.assignment_question_id) ? '2px solid #4caf50' : '1px solid #e0e0e0',
                backgroundColor: Array.from(answeredQuestions).includes(currentItem.assignment_question_id) ? 'rgba(76, 175, 80, 0.05)' : 'white'
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  {currentItem.text}
                </Typography>

                {/* Render question based on content type */}
                {currentItem.content?.question_type === 'multiple_choice' && currentItem.content.options && (
                  <Box>
                    {currentItem.content.options.map((option: string, optionIndex: number) => (
                      <Button
                        key={optionIndex}
                        variant={answers[currentItem.assignment_question_id] === option ? 'contained' : 'outlined'}
                        onClick={() => handleAnswerChange(currentItem.id, currentItem.assignment_question_id, option)}
                        sx={{
                          display: 'block',
                          mb: 1,
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          py: 1.5,
                          px: 2
                        }}
                        fullWidth
                      >
                        {option}
                      </Button>
                    ))}
                  </Box>
                )}

                {/* Fallback for legacy content.type format */}
                {currentItem.content?.type === 'multiple_choice' && currentItem.content.options && (
                  <Box>
                    {currentItem.content.options.map((option: string, optionIndex: number) => (
                      <Button
                        key={optionIndex}
                        variant={answers[currentItem.assignment_question_id] === option ? 'contained' : 'outlined'}
                        onClick={() => handleAnswerChange(currentItem.id, currentItem.assignment_question_id, option)}
                        sx={{
                          display: 'block',
                          mb: 1,
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          py: 1.5,
                          px: 2
                        }}
                        fullWidth
                      >
                        {String.fromCharCode(65 + optionIndex)}. {option}
                      </Button>
                    ))}
                  </Box>
                )}

                {/* Text Input Questions */}
                {(currentItem.content?.question_type === 'text_input' || currentItem.content?.type === 'text_input') && (
                  <textarea
                    value={answers[currentItem.assignment_question_id] || ''}
                    onChange={(e) => handleAnswerChange(currentItem.id, currentItem.assignment_question_id, e.target.value)}
                    placeholder="Type your detailed answer here..."
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '12px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '16px',
                      fontFamily: 'inherit'
                    }}
                  />
                )}

                {/* True/False Questions */}
                {(currentItem.content?.question_type === 'true_false' || currentItem.content?.type === 'true_false') && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant={answers[currentItem.assignment_question_id] === 'true' ? 'contained' : 'outlined'}
                      onClick={() => handleAnswerChange(currentItem.id, currentItem.assignment_question_id, 'true')}
                      color="success"
                      sx={{ minWidth: 100 }}
                    >
                      True
                    </Button>
                    <Button
                      variant={answers[currentItem.assignment_question_id] === 'false' ? 'contained' : 'outlined'}
                      onClick={() => handleAnswerChange(currentItem.id, currentItem.assignment_question_id, 'false')}
                      color="error"
                      sx={{ minWidth: 100 }}
                    >
                      False
                    </Button>
                  </Box>
                )}

                {/* Reading Fluency (Audio Recording) */}
                {currentItem.content?.question_type === 'reading_fluency_individual' && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Instructions:</strong> {currentItem.content.instructions || 'Read the text aloud clearly and fluently.'}
                      </Typography>
                      {currentItem.content.evaluation_criteria && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Evaluation Criteria:</strong> {currentItem.content.evaluation_criteria.join(', ')}
                        </Typography>
                      )}
                    </Alert>

                    <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {currentItem.text}
                      </Typography>
                    </Paper>

                    <AudioRecorder
                      onAudioReady={(audioUrl, duration) => {
                        handleAnswerChange(currentItem.id, currentItem.assignment_question_id, {
                          type: 'audio_recording',
                          audio_url: audioUrl,
                          duration_seconds: duration,
                          status: 'completed',
                          timestamp: new Date().toISOString()
                        });
                      }}
                      onError={(error) => {
                        console.error('Audio recording error:', error);
                        setError(error);
                      }}
                      websocketUrl={WS_BASE_URL}
                      assessmentId={assignmentId || ''}
                      questionId={currentItem.id}
                      assignmentQuestionId={currentItem.assignment_question_id}
                      maxDuration={currentItem.content.max_duration_seconds || 120}
                      disabled={Array.from(answeredQuestions).includes(currentItem.assignment_question_id)}
                    />
                  </Box>
                )}

                {/* Speaking Questions (Audio Recording) */}
                {(currentItem.content?.question_type === 'speaking' ||
                  currentItem.content?.type === 'speaking' ||
                  currentSection?.section_name.toLowerCase().includes('speaking')) &&
                  !currentItem.content?.options &&
                  !currentItem.content?.question_type?.includes('choice') && (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Instructions:</strong> {currentItem.content?.instructions || 'Record your spoken response to this question. Speak clearly and provide a complete answer.'}
                        </Typography>
                        {currentItem.content?.evaluation_criteria && Array.isArray(currentItem.content.evaluation_criteria) && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Evaluation Criteria:</strong> {currentItem.content.evaluation_criteria.join(', ')}
                          </Typography>
                        )}
                      </Alert>

                      <AudioRecorder
                        onAudioReady={(audioUrl, duration) => {
                          handleAnswerChange(currentItem.id, currentItem.assignment_question_id, {
                            type: 'speaking_response',
                            audio_url: audioUrl,
                            duration_seconds: duration,
                            status: 'completed',
                            timestamp: new Date().toISOString()
                          });
                        }}
                        onError={(error) => {
                          console.error('Audio recording error:', error);
                          setError(error);
                        }}
                        websocketUrl={WS_BASE_URL}
                        assessmentId={assignmentId || ''}
                        questionId={currentItem.id}
                        assignmentQuestionId={currentItem.assignment_question_id}
                        maxDuration={currentItem.content?.max_duration_seconds || 180}
                        disabled={Array.from(answeredQuestions).includes(currentItem.assignment_question_id)}
                      />
                    </Box>
                  )}

                {/* Answer Status */}
                {Array.from(answeredQuestions).includes(currentItem.assignment_question_id) && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      ✅ <strong>Answer saved automatically</strong>
                    </Typography>
                  </Alert>
                )}
              </Paper>

              {/* Question Navigation Button - Only Next (No Previous) */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Question {currentQuestionIndex + 1} of {allQuestions.length}
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleNextQuestion}
                  disabled={
                    currentQuestionIndex === allQuestions.length - 1 ||
                    !Array.from(answeredQuestions).includes(currentItem.assignment_question_id)
                  }
                  size="large"
                >
                  Next Question
                </Button>
              </Box>

              {/* Warning if question not answered */}
              {!Array.from(answeredQuestions).includes(currentItem.assignment_question_id) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ℹ️ Please answer this question before proceeding to the next one.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Section Completion */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Complete {currentSection.section_name}?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentSection.question_groups.flatMap(g => g.child_questions).filter(q => answeredQuestions.has(q.assignment_question_id)).length} of {currentSection.question_groups.flatMap(g => g.child_questions).length} questions answered
            </Typography>
          </Box>

          {!canProceedToNextSection() && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please answer all questions in this section before proceeding.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleRequestNextSection}
              disabled={!canProceedToNextSection()}
              endIcon={<SkipNext />}
              sx={{ width: '100%' }}
            >
              {currentSection.section_number === currentSection.total_sections
                ? 'Complete Exam'
                : `Continue to Next Section`}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Your answers are saved automatically as you complete each question.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SectionBasedExamInterface;
